import {
  Component,
  computed,
  inject,
  ViewChild,
  TemplateRef,
  OnInit,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { TemplateStore } from '@features/catalog/store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import {
  DataTableComponent,
  ColumnDef,
  TableAction,
} from '@shared/components/data-table/data-table';
import { SolarTemplate } from '@features/catalog/models';
import { CreateTemplateDialog } from '@shared/components/create-template-dialog/create-template-dialog';
import { ToastService } from '@shared/services/toast.service';
import { provideIcons } from '@ng-icons/core';
import {
  lucideFileCog,
  lucideZap,
  lucideEuro,
  lucideCalendar,
  lucideComponent,
} from '@ng-icons/lucide';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';

@Component({
  selector: 'app-templates-list',
  standalone: true,
  imports: [
    CommonModule,
    DataTableComponent,
    CreateTemplateDialog,
  ],
  providers: [
    provideIcons({
      lucideFileCog,
      lucideZap,
      lucideEuro,
      lucideCalendar,
      lucideComponent,
    }),
  ],
  templateUrl: './templates-list.html',
  styleUrl: './templates-list.css',
})
export class TemplatesListComponent implements OnInit {
  private readonly templateStore = inject(TemplateStore);
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly toastService = inject(ToastService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);

  @ViewChild('createDialog') createDialog!: CreateTemplateDialog;
  @ViewChild('nameTpl', { static: true }) nameTpl!: TemplateRef<any>;
  @ViewChild('componentsTpl', { static: true })
  componentsTpl!: TemplateRef<any>;
  @ViewChild('installationCostsTpl', { static: true })
  installationCostsTpl!: TemplateRef<any>;
  @ViewChild('materialCostsTpl', { static: true })
  materialCostsTpl!: TemplateRef<any>;
  @ViewChild('dateTpl', { static: true }) dateTpl!: TemplateRef<any>;

  organizationId = computed(() => this.currentOrgStore.organizationId() || '');
  templates = computed(() => this.templateStore.allTemplates());
  isLoading = computed(() => this.templateStore.isLoading());

  // Create a map of template costs
  templateCosts = computed(() => {
    const templates = this.templates();
    const costsMap = new Map<
      string,
      { installation: number; material: number }
    >();

    templates.forEach((template: any) => {
      const installation =
        (template.acInstallationCosts || 0) +
        (template.scaffoldingCosts || 0) +
        (template.gridConnectionCosts || 0) +
        (template.otherCosts || 0);

      const material =
        (template.dcElectricMaterial || 0) +
        (template.additionalRoofSurcharge || 0);

      costsMap.set(template.id, { installation, material });
    });

    return costsMap;
  });

  columns: ColumnDef<SolarTemplate>[] = [];

  actions: TableAction<SolarTemplate>[] = [
    {
      label: 'Bearbeiten',
      action: (item) => this.editTemplate(item),
      visible: () => this.currentOrgStore.can('template', 'update'),
    },
    {
      label: 'Duplizieren',
      action: (item) => this.duplicateTemplate(item),
      visible: () => this.currentOrgStore.can('organization', 'createtemplate'),
    },
    {
      label: 'Löschen',
      type: 'destructive',
      action: (item) => this.deleteTemplate(item),
      visible: () => this.currentOrgStore.can('template', 'delete'),
      dividerBefore: true,
    },
  ];

  ngOnInit() {
    this.columns = [
      {
        header: 'Name & Typ',
        template: this.nameTpl,
      },
      {
        header: 'Komponenten',
        template: this.componentsTpl,
      },
      {
        header: 'Installationskosten',
        template: this.installationCostsTpl,
        class: 'w-[170px] text-right',
      },
      {
        header: 'Materialkosten',
        template: this.materialCostsTpl,
        class: 'w-[150px] text-right',
      },
      {
        header: 'Erstellt',
        template: this.dateTpl,
        class: 'w-[120px]',
      },
    ];
  }

  constructor() {
    // Convert organizationId signal to observable and connect it to the rxMethod
    // This approach avoids the circular dependency issue by using RxJS operators
    const orgId$ = toObservable(this.organizationId);

    // Subscribe the rxMethod to the observable
    // The rxMethod handles debouncing and prevents duplicate calls internally
    this.templateStore.loadTemplatesForOrg(orgId$);
  }

  addTemplate() {
    this.createDialog.open();
  }

  onTemplateCreated() {
    this.reloadTemplates();
  }

  onTemplateUpdated() {
    this.reloadTemplates();
  }

  private reloadTemplates(): void {
    const orgId = this.organizationId();
    if (orgId) {
      // Force reload by directly calling the rxMethod with the current org ID
      this.templateStore.loadTemplatesForOrg(orgId);
    }
  }

  editTemplate(template: SolarTemplate) {
    this.createDialog.open(template);
  }

  async duplicateTemplate(template: SolarTemplate) {
    try {
      const orgId = this.organizationId();
      if (orgId) {
        await this.templateStore.duplicateTemplate(
          orgId,
          template.id,
          `${template.name} (Kopie)`,
        );
        this.toastService.success('Vorlage erfolgreich dupliziert');
      }
    } catch (error) {
      this.toastService.error('Fehler beim Duplizieren der Vorlage');
      console.error('Error duplicating template:', error);
    }
  }

  async syncTemplates() {
    try {
      const orgId = this.organizationId();
      if (orgId) {
        await this.templateStore.syncTemplates(orgId);
      }
    } catch (error) {
      console.error('Error syncing templates:', error);
    }
  }

  async deleteTemplate(template: SolarTemplate) {
    const confirmed = await this.confirmationDialog.confirm({
      title: 'Vorlage löschen',
      description: `Möchten Sie die Vorlage "${template.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      cancelText: 'Abbrechen',
      type: 'warning',
    });
    if (confirmed) {
      try {
        const orgId = this.organizationId();
        if (orgId) {
          await this.templateStore.deleteTemplate(orgId, template.id);
          this.toastService.success('Vorlage erfolgreich gelöscht');
        }
      } catch (error) {
        this.toastService.error('Fehler beim Löschen der Vorlage');
        console.error('Error deleting template:', error);
      }
    }
  }

  getTemplateTypeLabel(type?: string): string {
    if (!type) return '-';
    return type === 'purchase' ? 'Kauf' : type === 'lease' ? 'Miete' : type;
  }

  getInstallationCosts(templateId: string | undefined): number {
    if (!templateId) return 0;
    return this.templateCosts().get(templateId)?.installation || 0;
  }

  getMaterialCosts(templateId: string | undefined): number {
    if (!templateId) return 0;
    return this.templateCosts().get(templateId)?.material || 0;
  }

  calculateTotalCosts(template: any): number {
    return (
      (template.dcElectricMaterial || 0) +
      (template.acInstallationCosts || 0) +
      (template.scaffoldingCosts || 0) +
      (template.additionalRoofSurcharge || 0) +
      (template.gridConnectionCosts || 0) +
      (template.otherCosts || 0)
    );
  }
}
